"use client";

import * as React from "react";
import { ArrowDown, ArrowUp, Check, SlidersHorizontal } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// The label shown for a column in the menu. Falls back to the column def
// header when it is a plain string (all of our job columns are).
export function columnLabel(column) {
  const { meta, header } = column.columnDef;
  if (meta?.label) return meta.label;
  if (typeof header === "string" && header !== "") return header;
  return null;
}

/**
 * Move one column a single place along `order`.
 *
 * `movableIds` is the set of columns this menu actually lists. Anything else —
 * a row's action menu, the favourite star, the unlabelled delete button — is
 * stepped over rather than swapped with, so those affordances keep the slot
 * their column def gave them however the user shuffles the rest. The user has
 * no control for them in the menu, so they must not move as a side effect of
 * moving something else.
 *
 * Returns `order` unchanged when there is nothing to swap with, which is the
 * same condition the menu disables the button on.
 */
export function moveColumn(order, movableIds, columnId, direction) {
  const from = order.indexOf(columnId);
  if (from === -1) return order;

  const movable = new Set(movableIds);
  const step = direction === "earlier" ? -1 : 1;

  let to = from + step;
  while (to >= 0 && to < order.length && !movable.has(order[to])) to += step;
  if (to < 0 || to >= order.length) return order;

  const next = [...order];
  next[from] = order[to];
  next[to] = columnId;
  return next;
}

// "Columns" popover for a TanStack table. Built on the popover primitive rather
// than a dropdown menu so it uses the Radix packages this app already ships.
//
// Each row is a show/hide toggle plus a pair of move buttons. The toggles are
// `role="checkbox"`, not `role="menuitemcheckbox"`: this is a plain list of
// controls inside a labelled group, not a menu. `menuitemcheckbox` is only
// valid when owned by a `role="menu"` element and would oblige us to implement
// full menu keyboard semantics (arrow-key roving focus, typeahead). As
// checkboxes, Tab moves between them and Space/Enter toggles, which is what the
// markup actually does. Radix's popover still handles Escape-to-close and
// returning focus to the trigger.
//
// The move buttons are siblings of the toggle inside a plain row element, not
// children of it: a button cannot contain another button, and `role="group"`
// happily owns ordinary buttons alongside its checkboxes. Each one names its
// column ("Move Location earlier") so it stands on its own in a screen reader's
// list of controls, and is disabled at the end of the list it can move within.
//
// Moving a column changes only where it sits left to right. Which columns fold
// away on a narrow screen is governed by `meta.priority`, independently — see
// the note on the collapsing logic in data-table.jsx.
export function DataTableViewOptions({ table, className }) {
  const leafColumns = table.getAllLeafColumns();
  const columns = leafColumns.filter(
    (column) => column.getCanHide() && columnLabel(column)
  );

  // `getAllLeafColumns` is already ordered by `columnOrder`, so this is the
  // table's current left-to-right order.
  const order = leafColumns.map((column) => column.id);
  const movableIds = columns.map((column) => column.id);

  const move = (columnId, direction) =>
    table.setColumnOrder(moveColumn(order, movableIds, columnId, direction));

  if (columns.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "border-moblyze-grey-border-dark bg-moblyze-grey-dark text-white hover:border-moblyze-grey-border-dark hover:bg-moblyze-charcoal hover:text-white",
            className
          )}
        >
          <SlidersHorizontal className="text-white" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Columns"
        className="w-64 border-moblyze-grey-border-dark bg-moblyze-grey-dark p-1"
      >
        <div role="group" aria-label="Show, hide and reorder columns">
          {columns.map((column, index) => {
            const isVisible = column.getIsVisible();
            const label = columnLabel(column);
            return (
              <div
                key={column.id}
                className="flex w-full items-center gap-1 rounded-sm hover:bg-white/[0.07]"
              >
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isVisible}
                  onClick={() => column.toggleVisibility(!isVisible)}
                  className="flex min-w-0 flex-1 cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-white focus-visible:bg-white/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-moblyze-grey-dark"
                >
                  <Check
                    className={cn(
                      "shrink-0 text-white",
                      isVisible ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">{label}</span>
                </button>
                <MoveButton
                  label={`Move ${label} earlier`}
                  disabled={index === 0}
                  onClick={() => move(column.id, "earlier")}
                  icon={ArrowUp}
                />
                <MoveButton
                  label={`Move ${label} later`}
                  disabled={index === columns.length - 1}
                  onClick={() => move(column.id, "later")}
                  icon={ArrowDown}
                />
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MoveButton({ label, disabled, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-moblyze-grey-light transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-moblyze-grey-dark disabled:pointer-events-none disabled:opacity-30"
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

export default DataTableViewOptions;
